import React from "react";
import {connect} from "react-redux";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {Menu} from "antd";
import "./JobStatusStats.scss";
import StoreState from "../../../interfaces/store/storeState";
import {TaskStatusCount} from "../../../interfaces/store/actions/payload/job";
import {calculateStatusCount, getStatusIconData} from "../../../utils/common";
import {Status, StatusIconData} from "../../../interfaces/store/actions/payload/task";
import {SetStatusStatsVisibleAction} from "../../../interfaces/store/actions/layout";
import {setStatusStatsVisible} from "../../../store/actions/layout";
import {ENTER_KEY, SPACE_KEY} from "../../../utils/constants";

const { SubMenu } = Menu;

interface StatusNameMap {
    [key: string]: Status
}

interface StateProps {
    translate: TranslateFunction
    statusCount: TaskStatusCount
    statusStatsVisible: boolean
}

interface DispatchProps {
    setStatusStatsVisible(payload: boolean): SetStatusStatsVisibleAction
}

type Props = StateProps & DispatchProps;

class JobStatusStats extends React.Component<Props> {
    private static readonly translationPath: string = "components.JobStatusStats.JobStatusStats";

    private static readonly statusNameMap: StatusNameMap = {
        skipped: Status.SKIPPED,
        notStarted: Status.NOT_STARTED,
        ready: Status.READY,
        inProgress: Status.IN_PROGRESS,
        failed: Status.FAILED,
        reopened: Status.REOPENED,
        rejected: Status.REJECTED,
        completed: Status.COMPLETED,
    };

    constructor(props: Props) {
        super(props);
        this.createStatusStats = this.createStatusStats.bind(this);
        this.changeStatsVisibility = this.changeStatsVisibility.bind(this);
        this.toggleStats = this.toggleStats.bind(this);
    }

    /**
     * Toggle stats menu
     *
     * @param {React.KeyboardEvent} event
     * @return {void}
     */
    toggleStats(event: React.KeyboardEvent): void {
        if ([ENTER_KEY, SPACE_KEY].includes(event.key)) {
            this.changeStatsVisibility();
        }
    }

    changeStatsVisibility(): void {
        const {statusStatsVisible}: Props = this.props;
        this.props.setStatusStatsVisible(!statusStatsVisible);
    }

    createStatusStats(): React.ReactNode[] {
        const {statusNameMap}: typeof JobStatusStats = JobStatusStats;
        const {statusCount, translate}: Props = this.props;
        return (
            Object.keys(statusNameMap)
                .filter((status: string): boolean => statusCount[status as keyof TaskStatusCount] > 0)
                .map((status: string, index: number): React.ReactNode => {
                    const {statusClass, statusName}: StatusIconData = getStatusIconData({
                        status: statusNameMap[status],
                        isActiveTask: false,
                        activeTaskInvalid: false,
                        validTask: false,
                        translationPath: "components.TaskChanger.TaskChanger",
                        translate,
                    });
                    return (
                        <Menu.Item key={index}>
                            <div className="df-stats-item">
                                <span className={`df-status-icon ${statusClass}`} title={statusName}/>
                                <span className="df-status-name">{statusName}</span>
                                <span className="df-status-count">
                                    {statusCount[status as keyof TaskStatusCount]}
                                </span>
                            </div>
                        </Menu.Item>
                    );
                })
        );
    }

    render() {
        const {translationPath}: typeof JobStatusStats = JobStatusStats;
        const {statusStatsVisible}: Props = this.props;
        const openKeys: string[] = statusStatsVisible ? ["statusStats"] : [];

        return (
            <Menu
                mode="inline"
                className="df-job-status-stats"
                openKeys={openKeys}
                onOpenChange={this.changeStatsVisibility}
            >
                <SubMenu
                    key="statusStats"
                    title={(
                        <span
                            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                            tabIndex={0}
                            role="button"
                            onKeyDown={this.toggleStats}
                            className="df-stats-title"
                        >
                            <Translate id={`${translationPath}.taskStatus.text`}/>
                        </span>
                    )}
                >
                    {this.createStatusStats()}
                </SubMenu>
            </Menu>
        );
    }
}

function mapStateToProps(
    {
        localize,
        job: {
            jobPhase,
        },
        layout: {
            jobSection: {
                statusStatsVisible,
            },
        },
    }: StoreState,
): StateProps {
    return {
        translate: getTranslate(localize),
        statusCount: calculateStatusCount(jobPhase?.phaseStats),
        statusStatsVisible,
    };
}

const mapDispatchToProps: DispatchProps = {
    setStatusStatsVisible,
};

export default connect(mapStateToProps, mapDispatchToProps)(JobStatusStats);
